import { ftFormatCampaignTime, ftClockColsFromArr, ftClockCalendarFromArr, ftClockCampaignField } from '../shared/campaign-clock';
// render-my-campaigns.ts — the "catalog-my-campaigns" renderer: the GM dashboard
// (my_campaigns.php). The endpoint ships pure data; this renderer owns the compact
// card shell and management actions.

import type { CatalogListConfig } from './types';
import { campaignDashboardCards, requireCampaignList, parseCampaignRowsResponse, type DashboardCampaignCard as MyCampaignCard } from '../shared/campaign-list-rows';
interface CatalogMyCampaignsResponse { cards: MyCampaignCard[]; total: number; }
import { initTooltips, resolveUrl, results, setStatus, showLoader, hideLoader } from './catalog-list';
import { buildEmptyState, emptyStateFromAttributes } from '../shared/empty-state';

declare const copyInputToClipboard: ((inputId: string) => void) | undefined;

function icon(id: string, w = 20, h = 20, title: string | boolean = false): string {
    return typeof iconMap === 'function' ? iconMap(id, w, h, title) : '';
}

function appendIconButton(
    parent: HTMLElement,
    href: string,
    id: string,
    className: string,
    iconId: string,
    ariaLabel: string,
    title: string | boolean = false,
): HTMLAnchorElement {
    const a = document.createElement('a');
    a.href = href;
    a.id = id;
    a.className = className;
    a.setAttribute('aria-label', ariaLabel);
    a.innerHTML = icon(iconId, 20, 20, title);
    parent.appendChild(a);
    return a;
}

function appendActionButton(
    parent: HTMLElement,
    id: string,
    className: string,
    iconId: string,
    ariaLabel: string,
    title: string | boolean,
    display: string,
    onClick: () => void | Promise<void>,
): HTMLButtonElement {
    const button = document.createElement('button');
    button.id = id;
    button.type = 'button';
    button.className = className;
    button.style.display = display;
    button.setAttribute('aria-label', ariaLabel);
    button.innerHTML = icon(iconId, 20, 20, title);
    button.addEventListener('click', () => { void onClick(); });
    parent.appendChild(button);
    return button;
}

function appendBadge(parent: HTMLElement, className: string, text: string): HTMLSpanElement {
    const badge = document.createElement('span');
    badge.className = className;
    badge.textContent = text;
    parent.appendChild(badge);
    return badge;
}

function appendIconBadge(parent: HTMLElement, iconId: string, label: string): void {
    const badge = document.createElement('span');
    badge.className = 'badge bg-warning text-dark';
    badge.setAttribute('aria-label', label);
    badge.innerHTML = icon(iconId, 18, 18, label);
    parent.appendChild(badge);
}

function appendActionRow(parent: HTMLElement): HTMLDivElement {
    const row = document.createElement('div');
    row.className = 'border-top border-secondary border-opacity-25 flex-shrink-0 px-3 py-2';
    const group = document.createElement('div');
    group.className = 'd-flex flex-wrap align-items-center gap-1';
    row.appendChild(group);
    parent.appendChild(row);
    return group;
}

type ChipVariant = 'success' | 'info' | 'warning' | 'danger';

const CHIP_STYLES: Record<ChipVariant, { rgb: string; dateClass: string; timeClass: string }> = {
    success: { rgb: '25, 135, 84', dateClass: 'bg-secondary-subtle text-secondary-emphasis', timeClass: 'bg-info-subtle text-info-emphasis' },
    info: { rgb: '13, 202, 240', dateClass: 'bg-info-subtle text-info-emphasis', timeClass: 'bg-secondary-subtle text-secondary-emphasis' },
    warning: { rgb: '255, 193, 7', dateClass: 'bg-warning-subtle text-warning-emphasis', timeClass: 'bg-secondary-subtle text-secondary-emphasis' },
    danger: { rgb: '220, 53, 69', dateClass: 'bg-danger-subtle text-danger-emphasis', timeClass: 'bg-secondary-subtle text-secondary-emphasis' },
};

function buildDateTimeChip(iconId: string, iconTitle: string, dateText: string, timeText = '', variant: ChipVariant = 'success'): HTMLElement {
    const style = CHIP_STYLES[variant];
    const chip = document.createElement('span');
    chip.className = 'd-inline-flex align-items-center gap-1 rounded flex-wrap';
    chip.style.padding = '0.1rem 0.4rem';
    chip.style.background = `linear-gradient(90deg, rgba(${style.rgb}, 0.28), rgba(${style.rgb}, 0.05))`;
    chip.style.border = `1px solid rgba(${style.rgb}, 0.55)`;

    const chipIcon = document.createElement('span');
    chipIcon.className = 'd-inline-flex align-items-center';
    chipIcon.innerHTML = icon(iconId, 16, 16, iconTitle);
    chip.appendChild(chipIcon);

    const dateBadge = document.createElement('span');
    dateBadge.className = `badge rounded-pill ${style.dateClass} fw-normal`;
    dateBadge.style.fontSize = '0.7rem';
    dateBadge.style.padding = '0.15em 0.45em';
    dateBadge.textContent = dateText;
    chip.appendChild(dateBadge);

    if (timeText) {
        const timeBadge = document.createElement('span');
        timeBadge.className = `badge rounded-pill ${style.timeClass} fw-normal`;
        timeBadge.style.fontSize = '0.7rem';
        timeBadge.style.padding = '0.15em 0.45em';
        timeBadge.textContent = timeText;
        chip.appendChild(timeBadge);
    }

    return chip;
}

function buildReferenceChip(iconId: string, iconTitle: string, label: string, variant: ChipVariant, onClick: () => void): HTMLButtonElement {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'btn btn-sm p-0 border-0 bg-transparent align-baseline text-decoration-none shadow-none';
    button.dataset.ftRefChip = iconId;
    button.appendChild(buildDateTimeChip(iconId, iconTitle, label, '', variant));
    button.addEventListener('click', onClick);
    return button;
}

function updateInviteSidelog(inviteCode: string): void {
    if (typeof openSideLog === 'function') openSideLog();
    const target = document.getElementById('div_sidelog_warning');
    if (!target) return;

    target.innerHTML = '';
    const msg = document.createElement('h5');
    msg.className = 'bg-success text-dark rounded border border-2 border-danger p-3 my-2';
    msg.appendChild(document.createTextNode('Campaign Invite Link Copied to Clipboard'));
    msg.appendChild(document.createElement('br'));
    msg.appendChild(document.createTextNode(`Invite Code: ${inviteCode}`));
    target.appendChild(msg);
}

async function toggleRunning(card: MyCampaignCard, paused: string): Promise<void> {
    await queryData(resolveUrl('options/tools/campaigns/update/update_campaign_running_inc.php'), 'div_sidelog_warning', {
        campaign_id: card.item_id, campaign_end_date: paused, csrf_token: card.csrf_token,
    });
    window.location.replace(resolveUrl('my_campaigns.php'));
}

function appendCampaignControls(parent: HTMLElement, card: MyCampaignCard): void {
    const campaignId = card.item_id;
    const privacyUrl = resolveUrl('options/db_queries/update/update_privacy_inc.php');
    const deleteUrl = resolveUrl('options/db_queries/delete/soft_delete_inc.php');

    const stopButton = appendActionButton(
        parent,
        `btn_toggle_campaign_stop_${campaignId}`,
        'btn btn-sm btn-warning',
        'pause_campaign',
        'Pause campaign',
        'Pause Campaign',
        card.is_running ? 'block' : 'none',
        () => {
            void toggleRunning(card, '1');
            stopButton.style.display = 'none';
            startButton.style.display = 'block';
        },
    );

    const startButton = appendActionButton(
        parent,
        `btn_toggle_campaign_start_${campaignId}`,
        'btn btn-sm btn-success',
        'restart_campaign',
        'Restart campaign',
        'Restart Campaign',
        card.is_running ? 'none' : 'block',
        () => {
            void toggleRunning(card, '0');
            startButton.style.display = 'none';
            stopButton.style.display = 'block';
        },
    );

    const publicButton = appendActionButton(
        parent,
        `campaign_public_${campaignId}`,
        'btn btn-primary btn-sm',
        'public',
        'Make private',
        'Public',
        card.is_private ? 'none' : 'block',
        async () => {
            await queryData(privacyUrl, 'div_sidelog_warning', {
                item_type: 'campaign',
                campaign_id: campaignId,
                campaign_private: '1',
                csrf_token: card.csrf_token,
            });
            publicButton.style.display = 'none';
            privateButton.style.display = 'block';
        },
    );

    const privateButton = appendActionButton(
        parent,
        `campaign_private_${campaignId}`,
        'btn btn-danger btn-sm',
        'private',
        'Make public',
        'Private',
        card.is_private ? 'block' : 'none',
        async () => {
            await queryData(privacyUrl, 'div_sidelog_warning', {
                item_type: 'campaign',
                campaign_id: campaignId,
                campaign_private: '0',
                csrf_token: card.csrf_token,
            });
            privateButton.style.display = 'none';
            publicButton.style.display = 'block';
        },
    );

    const deleteButton = appendActionButton(
        parent,
        `campaign_delete_${campaignId}`,
        'btn btn-warning btn-sm',
        'delete',
        'Delete campaign',
        'Delete',
        'block',
        () => {
            deleteButton.style.display = 'none';
            confirmButton.style.display = 'block';
        },
    );

    const confirmButton = appendActionButton(
        parent,
        `campaign_confirm_${campaignId}`,
        'btn btn-danger btn-sm',
        'confirm',
        'Confirm delete',
        'Confirm',
        'none',
        async () => {
            if (typeof openSideLog === 'function') openSideLog();
            await queryData(deleteUrl, 'div_sidelog_warning', {
                item_type: 'campaign',
                campaign_id: campaignId,
                display_success_sidelog: '1',
                csrf_token: card.csrf_token,
            }, true, true, true, `js-my-campaign-card-${campaignId}`);
        },
    );
}

function appendInfoRows(parent: HTMLElement, card: MyCampaignCard): void {
    const sessionRow = document.createElement('div');
    sessionRow.className = 'border-top border-secondary border-opacity-25 flex-shrink-0 px-3 py-2 small';
    const sessionTop = document.createElement('div');
    sessionTop.className = 'd-flex flex-wrap align-items-center gap-1';
    appendIconButton(sessionTop, card.sessions_url, `btn_modify_campaign_sessions_${card.item_id}`, 'btn btn-sm btn-info', 'modify_campaign_sessions', 'Modify campaign sessions');
    if (card.is_running) {
        sessionTop.classList.add('mb-1');
        const sessionText = document.createElement('span');
        sessionText.className = 'text-muted';
        sessionText.textContent = 'Next Session:';
        sessionTop.appendChild(sessionText);
        appendBadge(sessionTop, 'badge bg-info text-dark', card.next_session_number || 'None').dataset.ftCampaignMetric = 'next_session_number';
        if (card.next_session_date && card.next_session_date !== 'None') {
            const sessionDate = document.createElement('div');
            sessionDate.className = 'mt-1';
            sessionDate.dataset.ftCampaignMetric = 'next_session_date';
            sessionDate.appendChild(buildDateTimeChip('calendar_time', 'Session Time', card.next_session_date, card.next_session_time || ''));
            sessionRow.appendChild(sessionDate);
        }
    }
    sessionRow.prepend(sessionTop);
    parent.appendChild(sessionRow);

    const seatsRow = document.createElement('div');
    seatsRow.className = 'border-top border-secondary border-opacity-25 flex-shrink-0 px-3 py-2 small';
    appendBadge(seatsRow, 'badge bg-warning text-dark me-1', String(card.seats_filled ?? 0)).dataset.ftCampaignMetric = 'seats_filled';
    seatsRow.appendChild(document.createTextNode(' of '));
    appendBadge(seatsRow, 'badge bg-info text-dark mx-1', String(card.max_players ?? 0)).dataset.ftCampaignMetric = 'seats_max';
    seatsRow.appendChild(document.createTextNode(' Seats Filled'));
    parent.appendChild(seatsRow);

    const worldRow = document.createElement('div');
    worldRow.className = 'border-top border-secondary border-opacity-25 flex-shrink-0 px-3 py-2 small text-break';
    const refsGroup = document.createElement('div');
    refsGroup.className = 'd-flex flex-wrap align-items-center gap-1';
    // A campaign's world is a world LOCATION (2026-10-20: the legacy item_type='world' items were migrated), which the
    // item-info modal cannot resolve — open its World Book instead.
    const world = buildReferenceChip('world', 'World', card.world_name || 'World', 'info', () => {
        if (card.world_id) window.location.href = `${resolveUrl('location.php')}?location_id=${encodeURIComponent(card.world_id)}`;
    });
    refsGroup.appendChild(world);
    const calendar = buildReferenceChip('calendar', 'Calendar', card.calendar_name || 'Calendar', 'warning', () => {
        if (typeof openItemInfo === 'function') openItemInfo(card.calendar_id);
    });
    refsGroup.appendChild(calendar);
    worldRow.appendChild(refsGroup);
    if (card.client_campaign) {
        const currentDate = document.createElement('div');
        currentDate.className = 'mt-1';
        const clock = ftFormatCampaignTime(ftClockColsFromArr(card.client_campaign), ftClockCalendarFromArr(card.client_campaign));
        currentDate.appendChild(buildDateTimeChip('calendar_time', 'Calendar Time', clock.calendar_current_date_f, clock.calendar_current_full_date_f.split('|')[1]?.trim() ?? '', 'danger'));
        worldRow.appendChild(currentDate);
    }
    parent.appendChild(worldRow);

    const datesRow = document.createElement('div');
    datesRow.className = 'border-top border-secondary border-opacity-25 flex-shrink-0 px-3 py-2 small';
    const datesGroup = document.createElement('div');
    datesGroup.className = 'd-flex flex-wrap align-items-center gap-1';
    if (card.start_date) {
        datesGroup.appendChild(buildDateTimeChip('campaign_start_date', 'Start Date', card.start_date, '', 'success'));
    }
    if (card.end_date) {
        datesGroup.appendChild(buildDateTimeChip('campaign_end_date', 'End Date', card.end_date, '', 'danger'));
    }
    datesRow.appendChild(datesGroup);
    parent.appendChild(datesRow);
}

function renderMyCampaignCard(card: MyCampaignCard): HTMLElement {
    if (card.client_campaign) {
        card.level = String(ftLayerValue(card.client_campaign, 'campaign_level') ?? '');
        card.next_session_number = card.next_session_date && card.next_session_date !== 'None'
            ? String(Number(ftClockCampaignField(card.client_campaign, 'count_past_sessions') ?? 0) + 1) : 'None';
    }
    const col = document.createElement('div');
    col.className = 'col-12 col-md-6 col-lg-4 col-xl-3 p-2 js-my-campaign-card';
    col.id = `js-my-campaign-card-${card.item_id}`;
    col.dataset.campaignName = card.name || '';

    const shell = document.createElement('div');
    shell.className = 'card ft-my-campaign-card text-left w-100 shadow-sm border border-secondary border-opacity-25 d-flex flex-column';
    shell.setAttribute('data-bs-theme', 'dark');
    col.appendChild(shell);

    const heading = document.createElement('div');
    heading.className = 'ft-my-campaign-card__heading';
    shell.appendChild(heading);

    const titleLink = document.createElement('a');
    titleLink.href = card.url;
    titleLink.className = 'ft-my-campaign-card__title-link';
    titleLink.setAttribute('aria-label', `Open ${card.name || 'Untitled campaign'}`);
    heading.appendChild(titleLink);

    const title = document.createElement('h4');
    title.className = 'ft-my-campaign-card__title';
    title.textContent = card.name || 'Untitled';
    titleLink.appendChild(title);

    const imgLink = document.createElement('a');
    imgLink.href = card.url;
    imgLink.className = 'text-decoration-none flex-shrink-0';
    shell.appendChild(imgLink);

    const mediaFrame = document.createElement('div');
    mediaFrame.className = 'overflow-hidden w-100';
    mediaFrame.style.aspectRatio = '16 / 9';
    imgLink.appendChild(mediaFrame);

    const img = document.createElement('img');
    img.src = card.picture_url || '';
    img.className = 'w-100 h-100';
    img.alt = `${card.name || 'Campaign'} Header`;
    img.loading = 'lazy';
    img.decoding = 'async';
    img.style.objectFit = 'cover';
    img.style.objectPosition = 'center';
    mediaFrame.appendChild(img);

    const badgeRow = document.createElement('div');
    badgeRow.className = 'ft-my-campaign-card__badges d-flex align-items-start gap-1';
    const badges = document.createElement('div');
    badges.className = 'd-flex flex-wrap gap-1 min-w-0';
    appendBadge(badges, 'badge bg-primary', `Level ${card.level || ''}`.trim()).dataset.ftCampaignMetric = 'level';
    appendBadge(badges, `badge ${card.type_badge_class || 'bg-warning text-dark'}`, card.type_label || 'Campaign').dataset.ftCampaignMetric = 'type';
    badgeRow.appendChild(badges);
    const requirements = document.createElement('div');
    requirements.className = 'd-flex flex-wrap gap-1 ms-auto';
    if (card.mic_required) appendIconBadge(requirements, 'microphone_required', 'Microphone required');
    if (card.webcam_required) appendIconBadge(requirements, 'webcam_required', 'Webcam required');
    badgeRow.appendChild(requirements);
    shell.appendChild(badgeRow);

    const campaignRow = appendActionRow(shell);
    appendIconButton(campaignRow, card.url, `btn_my_campaign_${card.item_id}`, 'btn btn-sm btn-danger', 'view_my_campaign', 'View campaign dashboard');
    appendIconButton(campaignRow, card.locations_url, `btn_locations_${card.item_id}`, 'btn btn-sm btn-danger', 'locations', 'Locations');
    appendIconButton(campaignRow, card.encounters_url, `btn_encounters_${card.item_id}`, 'btn btn-sm btn-danger', 'campaign_encounters', 'Encounters');

    const playerRow = appendActionRow(shell);
    appendIconButton(playerRow, card.players_url, `btn_campaign_players_${card.item_id}`, 'btn btn-sm btn-success', 'view_campaign_players', 'Campaign players');
    if (card.requested_signup) {
        const requests = appendIconButton(playerRow, card.join_requests_url, `btn_campaign_join_requests_${card.item_id}`, 'btn btn-sm btn-success position-relative', 'view_campaign', 'Join requests', 'Join requests');
        if ((card.pending_join_requests || 0) > 0) {
            const pending = document.createElement('span');
            pending.className = 'position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger';
            pending.textContent = String(card.pending_join_requests);
            requests.appendChild(pending);
        }
    }
    const hiddenInvite = document.createElement('input');
    hiddenInvite.type = 'hidden';
    hiddenInvite.id = `input_hidden_campaign_invite_code_${card.item_id}`;
    hiddenInvite.value = card.invite_link || '';
    shell.appendChild(hiddenInvite);
    appendActionButton(playerRow, `btn_campaign_invite_code_${card.item_id}`, 'btn btn-sm btn-success', 'copy_campaign_invite_code', 'Copy campaign invite code', 'Copy Campaign Invite Code', 'block', () => {
        if (typeof copyInputToClipboard === 'function') copyInputToClipboard(hiddenInvite.id);
        updateInviteSidelog(card.invite_code || '');
    });
    appendIconButton(playerRow, card.public_url, `btn_view_campaign_${card.item_id}`, 'btn btn-sm btn-success', 'view_campaign', 'View public campaign');

    const settingsRow = appendActionRow(shell);
    appendIconButton(settingsRow, card.edit_url, `btn_modify_campaign_${card.item_id}`, 'btn btn-sm btn-info', 'edit_campaign', 'Edit campaign');
    appendIconButton(settingsRow, card.media_url, `btn_media_manager_campaign_${card.item_id}`, 'btn btn-sm btn-info', 'modify_picture_gallery', 'Media manager', 'Media Manager - header galleries + image/video upload');
    appendIconButton(settingsRow, card.settings_url, `btn_my_campaign_settings_${card.item_id}`, 'btn btn-sm btn-info', 'my_campaign_settings', 'Campaign settings');
    appendIconButton(settingsRow, card.share_settings_url, `btn_share_campaign_settings_${card.item_id}`, 'btn btn-sm btn-info', 'share_campaign_settings', 'Share campaign settings');

    const controlsRow = appendActionRow(shell);
    appendCampaignControls(controlsRow, card);

    appendInfoRows(shell, card);

    const response = document.createElement('div');
    response.id = `div_refresh_campaign_${card.item_id}`;
    shell.appendChild(response);

    return col;
}

interface BuiltCard { el: HTMLElement; text: string; }

export function mountCatalogMyCampaigns(root: HTMLElement, config: CatalogListConfig): void {
    const container = results(root);
    if (!container) {
        console.error('[catalog-my-campaigns] mount missing [data-ft-catalog-results]', root);
        return;
    }

    const searchInput = config.searchInputId
        ? (document.getElementById(config.searchInputId) as HTMLInputElement | null)
        : null;
    const emptyText = root.getAttribute('data-ft-empty-text') || 'No campaigns found.';

    void (async () => {
        const loadingTimer = setTimeout(() => showLoader(root, config), 250);
        try {
            const res = await fetch(resolveUrl(config.endpoint), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: '{}',
            });
            const raw = await res.text();
            const payload = requireCampaignList(parseCampaignRowsResponse(raw, res.status, 'campaign-list-v1'));
            const data = { cards: campaignDashboardCards(payload, resolveUrl('')), total: payload.total };
            clearTimeout(loadingTimer);
            hideLoader(root);
            setStatus(root, '');
            build(container, data, searchInput, emptyText, root);
        } catch (e) {
            clearTimeout(loadingTimer);
            hideLoader(root);
            console.error('[catalog-my-campaigns] fetch/render failed', e);
            setStatus(root, '');
            container.innerHTML = '';
            const msg = document.createElement('div');
            msg.className = 'col-12 text-center text-muted py-4';
            msg.textContent = e instanceof Error ? e.message : 'Could not load campaigns. Please try again.';
            container.appendChild(msg);
        }
    })();
}

function build(
    container: HTMLElement,
    data: CatalogMyCampaignsResponse,
    searchInput: HTMLInputElement | null,
    emptyText: string,
    root: HTMLElement,
): void {
    container.innerHTML = '';

    const built: BuiltCard[] = [];
    for (const card of data.cards || []) {
        const el = renderMyCampaignCard(card);
        container.appendChild(el);
        built.push({ el, text: card.text || (card.name || '').toLowerCase() });
    }

    initTooltips(container);

    const emptyEl = document.createElement('div');
    emptyEl.className = 'col-12 text-center text-muted py-5';
    emptyEl.textContent = emptyText;
    emptyEl.style.display = built.length ? 'none' : '';
    container.appendChild(emptyEl);
    // No campaigns at all: the opted-in themed state replaces the plain line for good (a search over an
    // empty list still has nothing to show, so applyFilter leaves it alone).
    const emptySpec = built.length === 0 ? emptyStateFromAttributes(root, emptyText) : null;
    if (emptySpec) {
        emptyEl.className = 'col-12';
        emptyEl.replaceChildren(buildEmptyState(emptySpec));
    }

    const applyFilter = (): void => {
        const keyword = (searchInput?.value || '').trim().toLowerCase();
        let visible = 0;
        for (const c of built) {
            if (!c.el.isConnected) continue;
            const show = !keyword || c.text.includes(keyword);
            c.el.style.display = show ? '' : 'none';
            if (show) visible++;
        }
        if (!emptySpec) emptyEl.textContent = keyword ? 'No campaigns match your search.' : emptyText;
        emptyEl.style.display = visible === 0 ? '' : 'none';
    };

    const removalObserver = new MutationObserver((records) => {
        if (records.some((r) => r.removedNodes.length > 0)) applyFilter();
    });
    removalObserver.observe(container, { childList: true });

    if (searchInput) {
        const box = searchInput.closest('.ft-inline-search-root');
        const glyph = box?.querySelector('[data-ft-inline-search-role="glyph"]') ?? null;
        const clear = box?.querySelector('[data-ft-inline-search-role="clear"]') ?? null;
        const syncTrailing = (): void => {
            if (!glyph || !clear) return;
            const hasText = (searchInput.value || '').trim().length > 0;
            glyph.classList.toggle('d-none', hasText);
            clear.classList.toggle('d-none', !hasText);
        };
        searchInput.addEventListener('input', () => { syncTrailing(); applyFilter(); });
        if (clear) {
            clear.addEventListener('click', (e) => {
                e.preventDefault();
                searchInput.value = '';
                searchInput.focus();
                syncTrailing();
                applyFilter();
            });
        }
        syncTrailing();
    }

    applyFilter();
}
